// V9.24 · Contas a Pagar: previstos por urgência + baixa em 1 clique
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, Undo2 } from 'lucide-react';
import api from '@/lib/api';

const money = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const URG = {
  vencido: ['Vencido', 'bg-rose-100 text-rose-800 border-rose-300'],
  hoje: ['Vence hoje', 'bg-rose-50 text-rose-700 border-rose-200'],
  dois_dias: ['Vence em 2 dias', 'bg-amber-100 text-amber-800 border-amber-300'],
  a_vencer: ['A vencer', 'bg-slate-100 text-slate-700 border-slate-300'],
};

export const ContasPagarTab = ({ accounts = [], onChanged }) => {
  const [data, setData] = useState(null);
  const [filtro, setFiltro] = useState('all');
  const [loading, setLoading] = useState(true);
  const [pagando, setPagando] = useState(null);
  const [contaPg, setContaPg] = useState('');
  const [dataPg, setDataPg] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get('/fin/ledger/payables', {
        params: { status: filtro !== 'all' ? filtro : undefined },
      });
      setData(d);
    } catch (_) { toast.error('Erro ao carregar contas a pagar.'); }
    finally { setLoading(false); }
  }, [filtro]);

  useEffect(() => { load(); }, [load]);

  const pagar = async () => {
    if (!contaPg) return toast.error('Selecione a conta de pagamento.');
    setBusy(true);
    try {
      const { data: r } = await api.post(`/fin/ledger/entries/${pagando.id}/pay`,
        { account_id: contaPg, paid_at: dataPg });
      toast.success(r.message || 'Conta paga.');
      setPagando(null); load(); onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erro ao dar baixa.'); }
    finally { setBusy(false); }
  };

  const b = data?.por_urgencia || {};

  return (
    <div className="space-y-3" data-testid="tab-contas-pagar">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {[['vencido', 'Vencidas'], ['hoje', 'Vencem hoje'], ['dois_dias', 'Em até 2 dias'],
          ['a_vencer', 'A vencer']].map(([k, label]) => (
          <button key={k} type="button" data-testid={`pagar-bucket-${k}`}
            onClick={() => setFiltro(filtro === k ? 'all' : k)}
            className={`text-left rounded-xl border p-3 transition-transform hover:-translate-y-0.5 ${
              filtro === k ? 'ring-2 ring-primary' : ''} ${URG[k][1]}`}>
            <p className="text-[11px] font-bold uppercase tracking-wide">{label}</p>
            <p className="text-xl font-bold tabular-nums">{money(b[k])}</p>
          </button>
        ))}
        <div className="rounded-xl border p-3 bg-foreground/5">
          <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Total em aberto</p>
          <p className="text-xl font-bold tabular-nums" data-testid="pagar-total-aberto">
            {money(data?.total_aberto)}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base">
            Previstos em aberto {filtro !== 'all' && <Badge variant="outline" className="ml-2">{URG[filtro][0]}</Badge>}
          </CardTitle>
          {filtro !== 'all' && (
            <Button size="sm" variant="ghost" onClick={() => setFiltro('all')} data-testid="pagar-limpar-filtro">
              Ver todas
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Vencimento</TableHead><TableHead>Histórico</TableHead>
              <TableHead>Categoria</TableHead><TableHead>Fornecedor</TableHead>
              <TableHead className="text-right">Valor</TableHead><TableHead>Situação</TableHead>
              <TableHead>Ações</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={7} className="text-center py-8">Carregando…</TableCell></TableRow>}
              {!loading && !data?.items?.length && (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                  Nenhuma conta em aberto. Lance uma despesa com vencimento para ela aparecer aqui.
                </TableCell></TableRow>
              )}
              {!loading && (data?.items || []).map((r) => (
                <TableRow key={r.id} data-testid={`pagar-row-${r.id}`}>
                  <TableCell className="whitespace-nowrap text-xs font-medium">{r.due_date || '—'}</TableCell>
                  <TableCell className="max-w-[260px]"><p className="truncate">{r.historico}</p></TableCell>
                  <TableCell className="text-xs">{r.categoria_nome || '—'}</TableCell>
                  <TableCell className="text-xs">{r.fornecedor_cliente || '—'}</TableCell>
                  <TableCell className="text-right font-medium tabular-nums">{money(Math.abs(r.valor))}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={URG[r.urgencia][1]}>{URG[r.urgencia][0]}</Badge>
                  </TableCell>
                  <TableCell>
                    <Button size="sm" data-testid={`pagar-btn-${r.id}`}
                      onClick={() => { setPagando(r); setContaPg(accounts[0]?.id || ''); }}>
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Marcar como paga
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!pagando} onOpenChange={(v) => !v && setPagando(null)}>
        <DialogContent data-testid="dialog-pagar">
          <DialogHeader>
            <DialogTitle>Dar baixa em 1 clique</DialogTitle>
            <DialogDescription>
              {pagando?.historico} · {money(Math.abs(pagando?.valor))} · vence {pagando?.due_date}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Conta de pagamento *</Label>
              <Select value={contaPg} onValueChange={setContaPg}>
                <SelectTrigger data-testid="pagar-conta"><SelectValue placeholder="Selecione a conta" /></SelectTrigger>
                <SelectContent>
                  {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Data do pagamento</Label>
              <Input type="date" value={dataPg} onChange={(e) => setDataPg(e.target.value)}
                data-testid="pagar-data" />
            </div>
          </div>
          <div className="flex justify-between items-center pt-2">
            <Button variant="ghost" size="sm" className="text-muted-foreground"
              onClick={async () => {
                try {
                  await api.post(`/fin/ledger/entries/${pagando.id}/unpay`);
                  toast.success('Voltou para previsto.');
                  setPagando(null); load(); onChanged?.();
                } catch (err) { toast.error(err?.response?.data?.detail || 'Erro.'); }
              }} data-testid="pagar-desfazer">
              <Undo2 className="w-3 h-3 mr-1" /> Voltar para previsto
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setPagando(null)}>Cancelar</Button>
              <Button onClick={pagar} disabled={busy} data-testid="pagar-confirmar">
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Confirmar pagamento
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContasPagarTab;
